/*
 * ADELA adelaxo.com theme behaviours. One IIFE, no dependencies. Mirrors the React components of the clone:
 * header (scroll colour, drawer, anchors), shop (Next page, product modal from /products/<handle>.js),
 * cart (drawer, /cart/add.js, /cart/change.js, Section Rendering API), watch (scroll-snap slider, video modal),
 * signup (tags serialisation), footer (Cookie Choices no-op).
 */
(function () {
  "use strict";

  var config = window.adelaTheme || {};
  var routes = config.routes || {};

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        var value = attrs[key];
        if (value === null || value === undefined || value === false) return;
        if (key === "text") node.textContent = value;
        else if (key === "html") node.innerHTML = value;
        else if (key === "class") node.className = value;
        else node.setAttribute(key, value === true ? "" : value);
      });
    }
    (children || []).forEach(function (child) {
      if (child) node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
    });
    return node;
  }

  /* Shopify money format ("${{amount}}") applied to cents. */
  function formatMoney(cents) {
    var format = config.moneyFormat || "${{amount}}";
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split(".");
    var withComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + parts[1];
    return format
      .replace(/\{\{\s*amount\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, parts[0] + "," + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "," + parts[1])
      .replace(/<[^>]+>/g, "");
  }

  /* Shopify CDN URL with a width parameter. */
  function sized(src, width) {
    if (!src) return "";
    if (src.indexOf("cdn.shopify.com") === -1 && src.indexOf("/cdn/shop/") === -1) return src;
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  /* ------------------------------------------------------------------------
   * Header
   * ---------------------------------------------------------------------- */
  function initHeader() {
    var header = qs("[data-header]");
    if (!header) return;
    var toggle = qs("[data-header-toggle]", header);
    var drawer = qs("[data-header-drawer]", header);

    function onScroll() {
      header.classList.toggle("is-scrolled", window.scrollY > 0);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    function setOpen(open) {
      header.classList.toggle("is-open", open);
      if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
      if (drawer) {
        drawer.setAttribute("aria-hidden", open ? "false" : "true");
        if (open) drawer.removeAttribute("inert");
        else drawer.setAttribute("inert", "");
      }
    }

    if (toggle) {
      toggle.addEventListener("click", function () {
        setOpen(!header.classList.contains("is-open"));
      });
    }

    window.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && header.classList.contains("is-open")) setOpen(false);
    });

    qsa("[data-anchor-link]").forEach(function (link) {
      link.addEventListener("click", function (event) {
        setOpen(false);
        var target = document.getElementById(link.getAttribute("data-anchor-link"));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth" });
        history.pushState(null, "", "#" + link.getAttribute("data-anchor-link"));
      });
    });
  }

  /* ------------------------------------------------------------------------
   * Cart drawer and toggle (Section Rendering API re-renders the whole section)
   * ---------------------------------------------------------------------- */
  var cart = {
    open: false,
    root: function () {
      return qs("[data-cart-root]");
    },
    drawer: function () {
      return qs("[data-cart-drawer]");
    },
    applyState: function () {
      var drawer = cart.drawer();
      var toggle = qs("[data-cart-toggle]");
      var root = cart.root();
      if (!drawer) return;
      drawer.classList.toggle("is-active", cart.open);
      drawer.setAttribute("aria-hidden", cart.open ? "false" : "true");
      var count = root ? parseInt(root.getAttribute("data-item-count") || "0", 10) : 0;
      if (toggle) toggle.hidden = cart.open || count === 0;
    },
    setOpen: function (open) {
      cart.open = open;
      cart.applyState();
    },
    replaceSection: function (html) {
      var root = cart.root();
      if (!root || !html) return;
      var parsed = document.createElement("div");
      parsed.innerHTML = html;
      var next = qs("[data-cart-root]", parsed);
      if (!next) return;
      root.replaceWith(next);
      cart.applyState();
    },
    request: function (url, body) {
      return fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(Object.assign({}, body, { sections: "cart-drawer" })),
      }).then(function (response) {
        return response.json().then(function (json) {
          if (!response.ok) throw new Error(json.description || json.message || "Cart request failed");
          return json;
        });
      });
    },
    add: function (variantId, quantity) {
      return cart
        .request(routes.cartAdd || "/cart/add.js", { items: [{ id: variantId, quantity: quantity }] })
        .then(function (json) {
          cart.replaceSection(json.sections && json.sections["cart-drawer"]);
          cart.setOpen(true);
        });
    },
    change: function (key, quantity) {
      return cart
        .request(routes.cartChange || "/cart/change.js", { id: key, quantity: quantity })
        .then(function (json) {
          cart.replaceSection(json.sections && json.sections["cart-drawer"]);
        });
    },
  };

  function initCart() {
    cart.applyState();

    document.addEventListener("click", function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest("[data-cart-toggle]")) {
        cart.setOpen(true);
        return;
      }
      if (target.closest("[data-cart-close]")) {
        cart.setOpen(false);
        return;
      }
      var step = target.closest("[data-cart-step]");
      if (step) {
        var item = step.closest("[data-line-key]");
        var current = parseInt(item.getAttribute("data-line-quantity") || "0", 10);
        var delta = parseInt(step.getAttribute("data-cart-step"), 10);
        cart.change(item.getAttribute("data-line-key"), Math.max(0, current + delta)).catch(console.error);
      }
    });

    document.addEventListener("change", function (event) {
      var input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.hasAttribute("data-cart-quantity")) return;
      var item = input.closest("[data-line-key]");
      var parsed = parseInt(input.value, 10);
      if (isNaN(parsed)) return;
      cart.change(item.getAttribute("data-line-key"), Math.max(0, parsed)).catch(console.error);
    });

    document.addEventListener(
      "blur",
      function (event) {
        var input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.hasAttribute("data-cart-quantity")) return;
        if (input.value.trim() === "") {
          var item = input.closest("[data-line-key]");
          cart.change(item.getAttribute("data-line-key"), 0).catch(console.error);
        }
      },
      true,
    );

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && cart.open) cart.setOpen(false);
    });
  }

  /* ------------------------------------------------------------------------
   * Product modal (Shopify Buy Button replica), data from /products/<handle>.js
   * ---------------------------------------------------------------------- */
  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    var base = (routes.root || "/").replace(/\/$/, "");
    return fetch(base + "/products/" + handle + ".js", { headers: { Accept: "application/json" } })
      .then(function (response) {
        if (!response.ok) throw new Error("Product not found: " + handle);
        return response.json();
      })
      .then(function (product) {
        productCache[handle] = product;
        return product;
      });
  }

  function chevron() {
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", "20");
    svg.setAttribute("height", "36");
    svg.setAttribute("viewBox", "0 0 20 36");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "#000");
    svg.setAttribute("stroke-width", "1.5");
    svg.setAttribute("aria-hidden", "true");
    var path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M2 2l16 16L2 34");
    svg.appendChild(path);
    return svg;
  }

  function selectIcon() {
    var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "shopify-buy__select-icon");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("aria-hidden", "true");
    var path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M21 5.176l-9.086 9.353L3 5.176.686 7.647 12 19.382 23.314 7.647 21 5.176z");
    svg.appendChild(path);
    return svg;
  }

  var modal = {
    node: null,
    close: function () {
      if (modal.node) {
        modal.node.remove();
        modal.node = null;
      }
    },
    open: function (product) {
      modal.close();
      var hasOptions = !(product.options.length === 1 && product.options[0].name === "Title" && product.options[0].values.length === 1);
      var images = product.images.length ? product.images.slice() : [product.featured_image].filter(Boolean);
      var state = { imageIndex: 0, quantity: "1", selected: {} };
      (product.variants[0] ? product.variants[0].options : []).forEach(function (value, index) {
        state.selected[product.options[index].name] = value;
      });

      function currentVariant() {
        var exact = product.variants.filter(function (variant) {
          return variant.options.every(function (value, index) {
            return state.selected[product.options[index].name] === value;
          });
        })[0];
        return exact || product.variants[0];
      }

      var overlay = el("div", { class: "shopify-buy__modal-overlay is-active" });
      var dialog = el("div", { class: "shopify-buy__modal", role: "dialog", "aria-modal": "true", "aria-label": product.title });
      var closeBtn = el("button", { type: "button", class: "shopify-buy__btn--close", "aria-label": "Close" }, [el("span", { "aria-hidden": "true", html: "&times;" })]);
      var card = el("div", { class: "has-image shopify-buy__layout-horizontal shopify-buy__product" });

      var mainImg = el("img", { class: "shopify-buy__product__variant-img", src: sized(images[0], 560), alt: product.title });
      var prev = el("button", { type: "button", class: "carousel-button carousel-button--previous" }, ["Left", chevron()]);
      var next = el("button", { type: "button", class: "carousel-button carousel-button--next" }, ["Right", chevron()]);
      var mainWrap = el("div", { class: "main-image-wrapper" }, [prev, mainImg, next]);
      var carousel = el("div", { class: "shopify-buy__carousel" });
      var thumbs = images.map(function (src, index) {
        var thumb = el("a", { href: src, class: "shopify-buy__carousel-item", "aria-label": product.title, style: "background-image: url(" + sized(src, 200) + ")" });
        thumb.addEventListener("click", function (event) {
          event.preventDefault();
          state.imageIndex = index;
          renderImage();
        });
        carousel.appendChild(thumb);
        return thumb;
      });
      var imgWrapper = el("div", { class: "shopify-buy__product-img-wrapper" }, [mainWrap, carousel]);

      var title = el("h1", { class: "shopify-buy__product__title", text: product.title });
      var priceText = el("span", { class: "shopify-buy__product__actual-price" });
      var price = el("div", { class: "shopify-buy__product__price" }, [el("span", { class: "visuallyhidden", html: "Regular price&nbsp;" }), priceText]);

      var selectors = null;
      if (hasOptions) {
        selectors = el("div", { class: "shopify-buy__product__variant-selectors" });
        product.options.forEach(function (option, index) {
          var id = "shopify-buy__option-" + product.handle + "-" + index;
          var select = el("select", { id: id, class: "shopify-buy__option-select__select", name: option.name });
          option.values.forEach(function (value) {
            select.appendChild(el("option", { value: value, text: value }));
          });
          select.value = state.selected[option.name] || option.values[0];
          select.addEventListener("change", function () {
            state.selected[option.name] = select.value;
            var variant = currentVariant();
            if (variant.featured_image && variant.featured_image.src) {
              var match = images.indexOf(variant.featured_image.src);
              if (match >= 0) state.imageIndex = match;
            }
            renderImage();
            renderVariant();
          });
          selectors.appendChild(
            el("div", { class: "shopify-buy__option-select" }, [
              el("label", { for: id, class: "shopify-buy__option-select__label visuallyhidden", text: option.name }),
              el("div", { class: "shopify-buy__option-select-wrapper" }, [select, selectIcon()]),
            ]),
          );
        });
      }

      var qty = el("input", { class: "shopify-buy__quantity", type: "number", min: "0", "aria-label": "Quantity", value: "1" });
      var addBtn = el("button", { type: "button", class: "shopify-buy__btn shopify-buy__beside-quantity" });
      var btnRow = el("div", { class: "shopify-buy__btn-and-quantity" }, [
        el("div", { class: "shopify-buy__quantity-container" }, [qty]),
        el("div", { class: "shopify-buy__btn-wrapper" }, [addBtn]),
      ]);
      var description = el("div", { class: "shopify-buy__product-description", html: product.description || "" });
      /* Merch is a presale handed over at the venue, so the modal says so under the description. */
      var collectionNote = el("p", {
        class: "shopify-buy__product-description shopify-buy__product-collection-note",
        text: "Presale: merch is not posted. Bring your order number to the merch stand at the show and collect it there.",
      });

      function renderImage() {
        var index = Math.min(state.imageIndex, images.length - 1);
        if (index < 0) return;
        mainImg.src = sized(images[index], 560);
        thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle("shopify-buy__carousel-item--selected", i === index);
        });
      }

      function renderVariant() {
        var variant = currentVariant();
        priceText.textContent = formatMoney(variant.price);
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? "PRESALE" : "Out of stock";
      }

      prev.addEventListener("click", function () {
        state.imageIndex = (state.imageIndex - 1 + images.length) % images.length;
        renderImage();
      });
      next.addEventListener("click", function () {
        state.imageIndex = (state.imageIndex + 1) % images.length;
        renderImage();
      });
      addBtn.addEventListener("click", function () {
        var parsed = parseInt(qty.value, 10);
        var quantity = Math.max(1, isNaN(parsed) ? 1 : parsed);
        addBtn.disabled = true;
        cart
          .add(currentVariant().id, quantity)
          .catch(function (error) {
            console.error(error);
            window.alert(error.message);
          })
          .then(function () {
            addBtn.disabled = !currentVariant().available;
          });
      });
      closeBtn.addEventListener("click", modal.close);
      overlay.addEventListener("click", function (event) {
        if (event.target === overlay) modal.close();
      });

      card.appendChild(imgWrapper);
      card.appendChild(title);
      card.appendChild(price);
      if (selectors) card.appendChild(selectors);
      card.appendChild(btnRow);
      card.appendChild(description);
      card.appendChild(collectionNote);
      dialog.appendChild(closeBtn);
      dialog.appendChild(card);
      overlay.appendChild(dialog);

      renderImage();
      renderVariant();
      var host = qs("[data-product-modal-root]") || document.body;
      host.appendChild(overlay);
      modal.node = overlay;
      closeBtn.focus();
    },
  };

  function initShop() {
    document.addEventListener("click", function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;

      var nextBtn = target.closest("[data-shop-next]");
      if (nextBtn) {
        var grid = nextBtn.closest("[data-shop]");
        qsa('.shopify-buy__product[data-page="2"]', grid).forEach(function (product) {
          product.hidden = false;
        });
        nextBtn.classList.remove("is-active");
        return;
      }

      var view = target.closest("[data-product-modal]");
      if (view) {
        var handle = view.getAttribute("data-product-modal");
        view.disabled = true;
        fetchProduct(handle)
          .then(modal.open)
          .catch(function (error) {
            console.error(error);
            var url = view.getAttribute("data-product-url");
            if (url) window.location.href = url;
          })
          .then(function () {
            view.disabled = false;
          });
      }
    });

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && modal.node) modal.close();
    });
  }

  /* ------------------------------------------------------------------------
   * WATCH slider and video modal
   * ---------------------------------------------------------------------- */
  function initWatch() {
    qsa("[data-watch]").forEach(function (section) {
      var track = qs("[data-watch-track]", section);
      var prev = qs("[data-watch-prev]", section);
      var next = qs("[data-watch-next]", section);
      var counter = qs("[data-watch-counter]", section);
      var currentEl = qs("[data-watch-current]", section);
      var totalEl = qs("[data-watch-total]", section);
      var template = qs("[data-watch-modal-template]", section);
      if (!track) return;

      var slides = track.children.length;
      var metrics = { step: 0, total: slides };
      var current = 1;
      var frame = 0;

      function measureStep() {
        var first = track.firstElementChild;
        if (!first) return 0;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      function render() {
        if (currentEl) currentEl.textContent = String(current);
        if (totalEl) totalEl.textContent = String(metrics.total);
        if (prev) prev.disabled = current === 1;
        if (next) next.disabled = current === metrics.total;
      }

      function syncCurrent() {
        if (metrics.step <= 0) return;
        current = Math.min(metrics.total, Math.max(1, Math.round(track.scrollLeft / metrics.step) + 1));
        render();
      }

      function measure() {
        var step = measureStep();
        if (step <= 0) return;
        var perView = Math.max(1, Math.round(track.clientWidth / step));
        metrics = { step: step, total: Math.max(1, slides - perView + 1) };
        if (counter) counter.classList.remove("is-unmeasured");
        syncCurrent();
      }

      new ResizeObserver(measure).observe(track);
      track.addEventListener(
        "scroll",
        function () {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(syncCurrent);
        },
        { passive: true },
      );

      function scrollByStep(direction) {
        if (metrics.step <= 0) return;
        track.scrollBy({ left: direction * metrics.step, behavior: "smooth" });
      }
      if (prev) prev.addEventListener("click", function () { scrollByStep(-1); });
      if (next) next.addEventListener("click", function () { scrollByStep(1); });

      /* Video modal: fixed dark overlay (z 502), close icon and Escape; backdrop click does not close. */
      var activeModal = null;
      var trigger = null;

      function closeModal() {
        if (!activeModal) return;
        activeModal.remove();
        activeModal = null;
        if (trigger) trigger.focus();
      }

      function openModal(id, title, button) {
        if (!template) return;
        closeModal();
        trigger = button;
        var fragment = template.content.cloneNode(true);
        var dialog = qs("[data-video-modal]", fragment);
        var iframe = qs("[data-video-iframe]", fragment);
        var closeBtn = qs("[data-video-close]", fragment);
        dialog.setAttribute("aria-label", title);
        iframe.src = "https://www.youtube-nocookie.com/embed/" + id;
        iframe.title = title;
        closeBtn.addEventListener("click", closeModal);
        document.body.appendChild(fragment);
        activeModal = qs("[data-video-modal]");
        closeBtn.focus();
      }

      qsa("[data-watch-play]", section).forEach(function (button) {
        button.addEventListener("click", function () {
          openModal(button.getAttribute("data-watch-play"), button.getAttribute("data-watch-title") || "", button);
        });
      });

      document.addEventListener("keydown", function (event) {
        if (!activeModal) return;
        if (event.key === "Escape") closeModal();
        if (event.key === "Tab" && document.activeElement === qs("[data-video-close]", activeModal)) event.preventDefault();
      });
    });
  }

  /* ------------------------------------------------------------------------
   * Sign up: serialise country, postcode and opt-ins into contact[tags]
   * ---------------------------------------------------------------------- */
  function initSignup() {
    var form = document.getElementById("ae-cform-email-reg-1");
    if (!form) return;
    /* Shopify binds hCaptcha to customer forms only on focus or change, or at DOMContentLoaded when this flag is set. */
    form.dataset.shopifyCaptcha = "true";
    form.addEventListener("submit", function () {
      var tags = qs("[data-signup-tags]", form);
      if (!tags) return;
      var list = tags.value ? tags.value.split(",").map(function (s) { return s.trim(); }).filter(Boolean) : [];
      var country = qs("[data-signup-country]", form);
      var postcode = qs("[data-signup-postcode]", form);
      if (country && country.value) list.push("country:" + country.value);
      if (postcode && postcode.value.trim()) list.push("zip:" + postcode.value.trim().replace(/,/g, " "));
      qsa("[data-signup-optin]", form).forEach(function (box) {
        if (box.checked) list.push(box.getAttribute("data-signup-optin"));
      });
      tags.value = list.join(", ");
    });
  }

  /* ------------------------------------------------------------------------
   * Footer: Cookie Choices is a no-op (Transcend banner is not part of the clone)
   * ---------------------------------------------------------------------- */
  function initFooter() {
    qsa("[data-cookie-choices]").forEach(function (link) {
      link.addEventListener("click", function (event) {
        event.preventDefault();
      });
    });
  }

  /* ------------------------------------------------------------------------
   * Product page (main-product): gallery and variant select without the modal
   * ---------------------------------------------------------------------- */
  function initProductPage() {
    qsa("[data-product-page]").forEach(function (root) {
      var json = qs("[data-product-json]", root);
      if (!json) return;
      var product = JSON.parse(json.textContent);
      var mainImg = qs("[data-product-main-image]", root);
      var thumbs = qsa("[data-product-thumb]", root);
      var selects = qsa("[data-product-option]", root);
      var idInput = qs("[data-product-variant-id]", root);
      var priceEl = qs("[data-product-price]", root);
      var addBtn = qs("[data-product-add]", root);
      var index = 0;

      function showImage(i) {
        if (!mainImg || !thumbs.length) return;
        index = (i + thumbs.length) % thumbs.length;
        mainImg.src = thumbs[index].getAttribute("data-product-thumb");
        thumbs.forEach(function (thumb, j) {
          thumb.classList.toggle("shopify-buy__carousel-item--selected", j === index);
        });
      }
      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener("click", function (event) {
          event.preventDefault();
          showImage(i);
        });
      });
      var prev = qs("[data-product-prev]", root);
      var next = qs("[data-product-next]", root);
      if (prev) prev.addEventListener("click", function () { showImage(index - 1); });
      if (next) next.addEventListener("click", function () { showImage(index + 1); });

      function update() {
        var values = selects.map(function (select) { return select.value; });
        var variant = product.variants.filter(function (v) {
          return v.options.every(function (value, i) { return values[i] === value; });
        })[0] || product.variants[0];
        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (addBtn) {
          /* The available label is whatever the section rendered, so one place decides what it says. */
          if (!addBtn.dataset.availableLabel) addBtn.dataset.availableLabel = addBtn.textContent.trim() || "PRESALE";
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? addBtn.dataset.availableLabel : "Out of stock";
        }
        if (variant.featured_image && variant.featured_image.src) {
          var match = thumbs.findIndex(function (thumb) {
            return thumb.getAttribute("data-product-image-src") === variant.featured_image.src;
          });
          if (match >= 0) showImage(match);
        }
      }
      selects.forEach(function (select) { select.addEventListener("change", update); });
      update();
      showImage(0);

      var form = qs("form", root);
      if (form && addBtn) {
        form.addEventListener("submit", function (event) {
          event.preventDefault();
          var qty = qs("[data-product-quantity]", root);
          var parsed = qty ? parseInt(qty.value, 10) : 1;
          addBtn.disabled = true;
          cart
            .add(parseInt(idInput.value, 10), Math.max(1, isNaN(parsed) ? 1 : parsed))
            .catch(function (error) {
              console.error(error);
              window.alert(error.message);
            })
            .then(update);
        });
      }
    });
  }

  function init() {
    initHeader();
    initCart();
    initShop();
    initWatch();
    initSignup();
    initFooter();
    initProductPage();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
